import { memo, useMemo, useState, type CSSProperties } from 'react'
import { Square } from 'lucide-react'
import type { HeatmapCell, SimulationInputs } from '../types'
import { getStrategyMeta } from '../lib/strategy'

const DEFAULT_IVS = '20, 25, 30, 35, 40, 50, 60'
const DEFAULT_RVS = '15, 20, 25, 30, 35, 40, 50'
const MAX_AXIS = 10

type Metric = 'excessReturn' | 'outperformProbability'

const METRICS: Record<Metric, { label: string; center: number; format: (v: number) => string }> = {
  excessReturn: {
    label: '平均 Excess Return',
    center: 0,
    format: (v) => (v >= 0 ? '+' : '') + (v * 100).toFixed(1) + '%',
  },
  outperformProbability: {
    label: '跑赢基准概率',
    center: 0.5,
    format: (v) => (v * 100).toFixed(0) + '%',
  },
}

function parseAxis(text: string): { values: number[]; error: string } {
  const parts = text.split(/[,，\s]+/).map((part) => part.trim()).filter(Boolean)
  const values = parts.map(Number)
  if (!parts.length) return { values: [], error: '至少输入一个值' }
  if (values.some((v) => !Number.isFinite(v) || v <= 0 || v > 300)) return { values: [], error: '请输入 0–300 之间的百分比数字' }
  const unique = [...new Set(values)].sort((a, b) => a - b)
  if (unique.length > MAX_AXIS) return { values: [], error: '最多 ' + MAX_AXIS + ' 个值' }
  return { values: unique.map((v) => v / 100), error: '' }
}

/** Diverging colour around the metric's neutral point, scaled to the largest deviation in the grid. */
function cellStyle(value: number, center: number, scale: number): CSSProperties {
  const magnitude = Math.min(1, Math.abs(value - center) / scale)
  const alpha = 0.08 + magnitude * 0.55
  return {
    background: value >= center
      ? 'rgb(var(--positive-rgb) / ' + alpha + ')'
      : 'rgb(var(--negative-rgb) / ' + alpha + ')',
  }
}

export const VolatilityHeatmap = memo(function VolatilityHeatmap({
  inputs,
  cells,
  running,
  progress,
  onRun,
  onCancel,
}: {
  inputs: SimulationInputs
  cells: HeatmapCell[]
  running: boolean
  progress: number
  onRun: (ivs: number[], rvs: number[]) => void
  onCancel: () => void
}) {
  const meta = getStrategyMeta(inputs.strategy)
  const [ivText, setIvText] = useState(DEFAULT_IVS)
  const [rvText, setRvText] = useState(DEFAULT_RVS)
  const [metric, setMetric] = useState<Metric>('excessReturn')
  const ivAxis = parseAxis(ivText)
  const rvAxis = parseAxis(rvText)
  const axisError = ivAxis.error || rvAxis.error

  // Render the grid the cells were computed on, not the (possibly edited) text boxes.
  const grid = useMemo(() => ({
    ivs: [...new Set(cells.map((c) => c.iv))].sort((a, b) => a - b),
    rvs: [...new Set(cells.map((c) => c.rv))].sort((a, b) => a - b),
    byKey: new Map(cells.map((c) => [c.rv + '-' + c.iv, c])),
  }), [cells])

  const { center, format, label } = METRICS[metric]
  const scale = Math.max(
    metric === 'excessReturn' ? 0.01 : 0.05,
    ...cells.map((c) => Math.abs(c[metric] - center)),
  )

  return (
    <article className="chart-card chart-card--wide">
      <div className="chart-heading">
        <div>
          <p className="eyebrow">VOLATILITY SURFACE</p>
          <h3>IV × Diffusion Vol 策略热力图</h3>
          <p>每格最多 2,000 条路径；数值为 {meta.shortLabel} 相对 {meta.benchmarkLabel} 的{label}，并显示 Monte Carlo 标准误。</p>
        </div>
      </div>

      <div className="heatmap-controls">
        <label className="field">
          <span className="field__label">Entry IV 网格 (%)</span>
          <input type="text" value={ivText} onChange={(e) => setIvText(e.target.value)} aria-invalid={ivAxis.error ? true : undefined} spellCheck={false} />
        </label>
        <label className="field">
          <span className="field__label">Diffusion σ 网格 (%)</span>
          <input type="text" value={rvText} onChange={(e) => setRvText(e.target.value)} aria-invalid={rvAxis.error ? true : undefined} spellCheck={false} />
        </label>
        <label className="field">
          <span className="field__label">显示指标</span>
          <select value={metric} onChange={(e) => setMetric(e.target.value as Metric)}>
            <option value="excessReturn">平均 Excess Return</option>
            <option value="outperformProbability">跑赢基准概率</option>
          </select>
        </label>
        {running ? (
          <button className="play-small play-small--danger" onClick={onCancel}>
            <Square size={12} fill="currentColor" /> 取消 {progress}%
          </button>
        ) : (
          <button className="play-small" disabled={Boolean(axisError)} onClick={() => onRun(ivAxis.values, rvAxis.values)}>
            计算热力图 · {ivAxis.values.length * rvAxis.values.length} 格
          </button>
        )}
      </div>
      {axisError && <p className="field__error">{axisError}</p>}

      {cells.length ? (
        <div className="heatmap-wrap" tabIndex={0} aria-label="热力图，可横向滚动">
          <div className="heatmap-grid" style={{ gridTemplateColumns: '64px repeat(' + grid.ivs.length + ', minmax(64px, 1fr))', minWidth: 64 + grid.ivs.length * 68 }}>
            <div className="heatmap-corner">σ ↓ / IV →</div>
            {grid.ivs.map((iv) => <div className="heatmap-header" key={iv}>IV {(iv * 100).toFixed(0)}%</div>)}
            {grid.rvs.map((rv) => (
              <div className="heatmap-row" key={rv}>
                <div className="heatmap-header">σ {(rv * 100).toFixed(0)}%</div>
                {grid.ivs.map((iv) => {
                  const cell = grid.byKey.get(rv + '-' + iv)
                  const value = cell?.[metric] ?? center
                  const detail = cell
                    ? 'Excess ' + METRICS.excessReturn.format(cell.excessReturn) + ' · 跑赢 ' + (cell.outperformProbability * 100).toFixed(1) + '% · MC SE ' + (cell.standardError * 100).toFixed(2) + '%'
                    : ''
                  return (
                    <div className="heatmap-cell" key={iv} style={cell ? cellStyle(value, center, scale) : undefined} title={detail} aria-label={'IV ' + (iv * 100).toFixed(0) + '%, σ ' + (rv * 100).toFixed(0) + '%: ' + detail}>
                      <strong>{cell ? format(value) : '—'}</strong>
                      <span>{cell ? (metric === 'excessReturn' ? 'SE ' + (cell.standardError * 100).toFixed(1) + '%' : 'Excess ' + METRICS.excessReturn.format(cell.excessReturn)) : ''}</span>
                    </div>
                  )
                })}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="heatmap-placeholder">点击“计算热力图”，观察 IV 与 diffusion volatility σ 的组合如何改变策略相对表现；若启用 Jump，总实现波动还包含跳跃方差。</div>
      )}
      <p className="micro-note">
        固定其他参数：μ {(inputs.drift * 100).toFixed(1)}%、{inputs.dte} DTE、IV model {inputs.ivModel}。
        颜色按本次网格中最大偏离自动缩放（绿色 = 优于 {metric === 'excessReturn' ? '0' : '50%'}）。热力图是敏感性研究，不代表各 IV/RV 组合的发生概率。
      </p>
    </article>
  )
})
